import React from "react";

export function Button({ variant = "default", size, className = "", as, children, ...props }) {
  const v = {
    default: "btn",
    accent: "btn btn-accent",
    accent2: "btn btn-accent2",
    danger: "btn btn-danger",
    ghost: "btn btn-ghost",
  }[variant];
  const cls = `${v} ${size === "sm" ? "btn-sm" : ""} ${className}`.trim();
  const Comp = as || "button";
  return (
    <Comp className={cls} {...props}>
      {children}
    </Comp>
  );
}

export function Input({ className = "", ...props }) {
  return <input className={`input ${className}`} {...props} />;
}

export function Textarea({ className = "", ...props }) {
  return <textarea className={`textarea ${className}`} {...props} />;
}

export function Label({ children, htmlFor, className = "" }) {
  return (
    <label className={`label ${className}`} htmlFor={htmlFor}>
      {children}
    </label>
  );
}

export function Field({ label, htmlFor, children, hint, error }) {
  return (
    <div className="mb-4">
      {label && <Label htmlFor={htmlFor}>{label}</Label>}
      {children}
      {hint && !error && <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{hint}</p>}
      {error && <p className="text-xs mt-1" style={{ color: "var(--danger)" }} data-testid="field-error">{error}</p>}
    </div>
  );
}

export function Panel({ children, className = "", as: Comp = "div", ...props }) {
  return (
    <Comp className={`panel ${className}`} {...props}>
      {children}
    </Comp>
  );
}

export function Tag({ tone = "", children, className = "", ...props }) {
  const t = tone ? `tag-${tone}` : "";
  return (
    <span className={`tag ${t} ${className}`} {...props}>
      {children}
    </span>
  );
}

export function Spinner({ size = 16 }) {
  return (
    <span
      className="inline-block animate-spin"
      style={{
        width: size,
        height: size,
        border: "2px solid var(--border-strong)",
        borderTopColor: "var(--accent)",
        borderRadius: "999px",
      }}
    />
  );
}

export function Skeleton({ className = "", style }) {
  return <div className={`skeleton ${className}`} style={style} />;
}

export function Checkbox({ checked, onChange, id, label, "data-testid": testId }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer select-none">
      <button
        type="button"
        id={id}
        role="checkbox"
        aria-checked={checked}
        data-testid={testId}
        onClick={() => onChange(!checked)}
        className="mt-0.5 flex items-center justify-center"
        style={{
          width: 18,
          height: 18,
          border: `1px solid ${checked ? "var(--accent)" : "var(--border-strong)"}`,
          background: checked ? "var(--accent)" : "transparent",
          flexShrink: 0,
        }}
      >
        {checked && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#04130f" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </button>
      <span className="text-sm" style={{ color: "var(--text-2)" }}>
        {label}
      </span>
    </label>
  );
}

export function SectionTitle({ eyebrow, title, children }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
      <div>
        {eyebrow && <div className="heading-eyebrow mb-1">{eyebrow}</div>}
        <h1 className="text-xl font-semibold" style={{ color: "var(--text)" }}>{title}</h1>
      </div>
      {children}
    </div>
  );
}
